
// services/auth-service.js - Updated with environment awareness

import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload
} from 'firebase/auth';
import { auth } from '../firebase-config';
import mixpanel from 'mixpanel-browser';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.apiToken = null;
    
    // Log current environment and API URL
    console.log(`🌐 Environment: ${process.env.REACT_APP_ENV || 'development'}`);
    console.log(`🔗 API URL: ${process.env.REACT_APP_API_BASE_URL}`);
    
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      if (user) {
        this.authenticateWithAPI();
      } else {
        this.apiToken = null;
        localStorage.removeItem('retailer_token');
      }
    });
  }

  async login(email, password) {
    try {
      console.log(`🔑 Attempting login for: ${email}`);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Firebase login successful');
      return userCredential.user;
    } catch (error) {
      console.error('❌ Firebase login failed:', error);
      throw new Error(this.getErrorMessage(error.code));
    }
  }

    async sendEmailVerification() {
    if (!this.currentUser) {
      throw new Error('No user is currently signed in');
    }

    try {
      await sendEmailVerification(this.currentUser);
      console.log('📧 Verification email sent');
    } catch (error) {
      console.error('❌ Error sending verification email:', error);
      throw error;
    }
  }

  async checkEmailVerification() {
    if (!this.currentUser) {
      return false;
    }

    try {
      // Reload user to get latest verification status
      await reload(this.currentUser);
      const user = auth.currentUser;
      
      if (user && user.emailVerified) {
        console.log('✅ Email verification confirmed');
        // Re-authenticate with API now that email is verified
        await this.authenticateWithAPI();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('❌ Error checking email verification:', error);
      return false;
    }
  }

  async logout() {
    try {
      await signOut(auth);
      this.apiToken = null;
      localStorage.removeItem('retailer_token');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  async authenticateWithAPI() {
    if (!this.currentUser) return null;

    try {
      console.log('🔑 Authenticating with API...');
      
      // Get Firebase ID token
      const idToken = await this.currentUser.getIdToken();
      console.log('✅ Got Firebase ID token');
      
      // Check if API base URL is set
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!apiBaseUrl) {
        throw new Error('REACT_APP_API_BASE_URL not set in environment variables');
      }
      
      console.log(`🌐 Calling API: ${apiBaseUrl}/auth/login`);
      
      // Determine if we're in local development
      const isLocal = apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1');
      
      if (isLocal) {
        console.log('🏠 Using local development API');
      } else {
        console.log('☁️ Using production API');
      }
      
      // Authenticate with your retailer API
      // Use 30 second timeout to handle Cloud Run cold starts
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        signal: AbortSignal.timeout(30000)
      });

      console.log(`📡 API Response Status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorText;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}`;
        }
        
        throw new Error(`Authentication failed: ${errorMessage}`);
      }

      const data = await response.json();
      console.log('✅ API Authentication successful');

      this.apiToken = data.access_token;
      localStorage.setItem('retailer_token', this.apiToken);

      // Identify the user in Mixpanel — email per-user, retailer as profile properties.
      // (A retailer may have multiple admin users; identify on email keeps them distinct.)
      // Also register retailer_id + company_name as super-properties so every event
      // is automatically taggable by retailer without joining to the people profile.
      try {
        if (mixpanel.__loaded && data.retailer && data.retailer.email) {
          mixpanel.identify(data.retailer.email);
          mixpanel.people.set({
            $email: data.retailer.email,
            $name: data.retailer.full_name,
            retailer_id: data.retailer.id,
            company_name: data.retailer.name,
            role: data.retailer.role,
          });
          mixpanel.register({
            retailer_id: data.retailer.id,
            company_name: data.retailer.name,
          });
        }
      } catch (_e) { /* tracking is best-effort */ }

      return data.retailer;
      
    } catch (error) {
      console.error('❌ API authentication error:', error);
      
      // More specific error messages
      if (error.name === 'AbortError') {
        throw new Error('API request timed out. Please check if the API is running.');
      } else if (error.message.includes('Failed to fetch')) {
        const apiUrl = process.env.REACT_APP_API_BASE_URL;
        const isLocal = apiUrl?.includes('localhost') || apiUrl?.includes('127.0.0.1');
        
        if (isLocal) {
          let host = apiUrl;
          try { host = new URL(apiUrl).host; } catch (_e) { /* fall back to full URL */ }
          throw new Error(`Cannot connect to local API. Make sure your Python services are running on ${host}`);
        } else {
          throw new Error('Cannot connect to API. Please check your internet connection.');
        }
      } else {
        throw error;
      }
    }
  }

  getApiToken() {
    return this.apiToken || localStorage.getItem('retailer_token');
  }

  async makeAuthenticatedRequest(url, options = {}) {
    const token = this.getApiToken();
    
    if (!token) {
      throw new Error('No authentication token available');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;

    console.log(`📡 Making authenticated request to: ${fullUrl}`);

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    };

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
        // 30 second timeout for cold starts; combined with the caller's abort signal when given
        signal: options.signal && typeof AbortSignal.any === 'function'
          ? AbortSignal.any([options.signal, AbortSignal.timeout(30000)])
          : AbortSignal.timeout(30000)
      });

      if (response.status === 401) {
        console.log('🔄 Token expired, refreshing...');
        if (this.currentUser) {
          await this.authenticateWithAPI();
          return this.makeAuthenticatedRequest(url, options);
        } else {
          throw new Error('Authentication required');
        }
      }

      return response;
      
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      throw error;
    }
  }

  getErrorMessage(errorCode) {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid email address or password',
      'auth/invalid-email': 'Invalid email address',
      'auth/user-disabled': 'This account has been disabled',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection'
    };

    return errorMessages[errorCode] || 'An unexpected error occurred';
  }
}

export const authService = new AuthService();